import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { API } from "@/lib/api";
import { Plus, X, DollarSign, FileText, Clock, Download, Send, Wand2 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const STATUS_COLORS = {
  draft: "#6B7280",
  sent: "#0B5FA5",
  paid: "#1F9D55",
  overdue: "#DC2626",
  void: "#9CA3AF",
};

const AdminInvoices = () => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ outstanding: 0, paid: 0, quoted: 0, count: 0 });
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    client_id: "", project_id: "", type: "invoice", notes: "", due_date: "",
    lines: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
  });

  const load = () => {
    api.get("/admin/invoices").then((r) => { setItems(r.data.invoices); setTotals(r.data.totals); }).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data.clients)).catch(() => {});
    api.get("/admin/projects").then((r) => setProjects(r.data.projects)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const updateLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    lines[i].total = Number((lines[i].quantity || 0) * (lines[i].unit_price || 0));
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { description: "", quantity: 1, unit_price: 0, total: 0 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const subtotal = form.lines.reduce((s, l) => s + (l.total || 0), 0);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, project_id: form.project_id || null };
      await api.post("/admin/invoices", payload);
      toast.success(`${form.type === "quote" ? "Quote" : "Invoice"} created`);
      setShowNew(false);
      setForm({ client_id: "", project_id: "", type: "invoice", notes: "", due_date: "",
        lines: [{ description: "", quantity: 1, unit_price: 0, total: 0 }] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (invoice, status) => {
    try {
      await api.patch(`/admin/invoices/${invoice.id}`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.id === invoice.id) setSelected({ ...selected, status });
    } catch { toast.error("Update failed"); }
  };

  const sendInvoice = async (invoice) => {
    try {
      await api.post(`/admin/invoices/${invoice.id}/send`);
      toast.success("Invoice sent — client notified by email + in-portal");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };

  const generateFromOrder = async () => {
    const orderType = window.prompt("Order type — 'inquiry' or 'reorder':", "reorder");
    if (!orderType || !["inquiry", "reorder"].includes(orderType)) return;
    const orderId = window.prompt(`Paste the ${orderType} ID:`);
    if (!orderId) return;
    try {
      await api.post("/admin/invoices/generate-from-order", { order_type: orderType, order_id: orderId });
      toast.success("Invoice generated from order");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Generate failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Finance</div>
          <h1 className="text-3xl font-bold tracking-tight">Quotes &amp; invoices.</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={generateFromOrder} className="ft-btn-secondary" data-testid="invoice-generate-from-order">
            <Wand2 size={16} /> Generate from Order
          </button>
          <button onClick={() => setShowNew(true)} className="ft-btn-primary" data-testid="new-invoice-btn">
            <Plus size={16} /> New Invoice / Quote
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="ft-card">
          <div className="ft-stat-label flex items-center justify-between">Outstanding <DollarSign size={16} style={{ color: "var(--ft-blue)" }} /></div>
          <div className="ft-stat-value mt-2" data-testid="fin-outstanding">{money(totals.outstanding)}</div>
        </div>
        <div className="ft-card">
          <div className="ft-stat-label flex items-center justify-between">Total Paid <DollarSign size={16} style={{ color: "var(--ft-success)" }} /></div>
          <div className="ft-stat-value mt-2" data-testid="fin-paid">{money(totals.paid)}</div>
        </div>
        <div className="ft-card">
          <div className="ft-stat-label flex items-center justify-between">Quoted <FileText size={16} style={{ color: "var(--ft-blue)" }} /></div>
          <div className="ft-stat-value mt-2" data-testid="fin-quoted">{money(totals.quoted)}</div>
        </div>
        <div className="ft-card">
          <div className="ft-stat-label flex items-center justify-between">Documents <Clock size={16} style={{ color: "var(--ft-muted)" }} /></div>
          <div className="ft-stat-value mt-2">{totals.count}</div>
        </div>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="admin-invoices-table">
          <thead>
            <tr><th>Number</th><th>Type</th><th>Client</th><th>Project</th><th>Total</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-[var(--ft-muted)] py-10">No invoices yet.</td></tr>}
            {items.map((inv) => (
              <tr key={inv.id} className="cursor-pointer" onClick={() => setSelected(inv)} data-testid={`invoice-row-${inv.id}`}>
                <td className="font-mono text-sm">{inv.number}</td>
                <td><span className="ft-badge">{inv.type}</span></td>
                <td>
                  <div className="font-semibold">{inv.client_name}</div>
                  <div className="text-xs text-[var(--ft-muted)]">{inv.client_company || inv.client_email}</div>
                </td>
                <td className="text-sm">{inv.project_name || "—"}</td>
                <td className="font-semibold">{money(inv.total)}</td>
                <td>
                  <span className="text-xs px-2 py-1 rounded-full font-semibold"
                        style={{ background: `${STATUS_COLORS[inv.status]}15`, color: STATUS_COLORS[inv.status] }}>
                    {inv.status}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    <a
                      href={`${API}/admin/invoices/${inv.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      className="p-1.5 rounded hover:bg-[var(--ft-light-gray)] text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
                      title="Download PDF"
                      data-testid={`invoice-pdf-${inv.id}`}
                    >
                      <Download size={14} />
                    </a>
                    {inv.status === "draft" && (
                      <button
                        onClick={() => sendInvoice(inv)}
                        className="p-1.5 rounded hover:bg-[var(--ft-light-gray)] text-[var(--ft-gold)]"
                        title="Send to client"
                        data-testid={`invoice-send-${inv.id}`}
                      >
                        <Send size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full my-8 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-2xl font-bold">New Document</h2>
              <button onClick={() => setShowNew(false)}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="space-y-4" data-testid="new-invoice-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Type</label>
                  <select className="ft-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="inv-type">
                    <option value="invoice">Invoice</option>
                    <option value="quote">Quote</option>
                  </select>
                </div>
                <div>
                  <label className="ft-label">Due Date</label>
                  <input type="date" className="ft-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="ft-label">Client*</label>
                <select required className="ft-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="inv-client">
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.company || c.email}</option>)}
                </select>
              </div>
              <div>
                <label className="ft-label">Project (optional)</label>
                <select className="ft-input" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                  <option value="">—</option>
                  {projects.filter((p) => p.client_id === form.client_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <div>
                <label className="ft-label">Line Items*</label>
                <div className="space-y-2">
                  {form.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_100px_100px_32px] gap-2 items-center">
                      <input required placeholder="Description" className="ft-input" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
                      <input required type="number" step="1" placeholder="Qty" className="ft-input" value={l.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} />
                      <input required type="number" step="0.01" placeholder="Unit" className="ft-input" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} />
                      <div className="text-sm font-semibold text-right">{money(l.total)}</div>
                      {form.lines.length > 1 && <button type="button" onClick={() => removeLine(i)} className="text-[var(--ft-muted)]"><X size={16}/></button>}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addLine} className="mt-2 text-sm font-semibold" style={{ color: "var(--ft-blue)" }} data-testid="inv-add-line">+ Add line</button>
              </div>

              <div className="pt-4 border-t border-[var(--ft-border)] flex justify-between items-center">
                <div className="text-sm text-[var(--ft-muted)]">Subtotal</div>
                <div className="text-2xl font-bold">{money(subtotal)}</div>
              </div>

              <div>
                <label className="ft-label">Notes</label>
                <textarea rows={2} className="ft-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>

              <button type="submit" className="ft-btn-primary w-full justify-center" data-testid="inv-create">Create {form.type === "quote" ? "Quote" : "Invoice"}</button>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="invoice-detail">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="ft-eyebrow">{selected.type}</div>
                <div className="font-mono text-lg mt-1">{selected.number}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={20} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="text-[var(--ft-muted)]">Client:</span> {selected.client_name} ({selected.client_email})</div>
              {selected.project_name && <div><span className="text-[var(--ft-muted)]">Project:</span> {selected.project_name}</div>}
              {selected.due_date && <div><span className="text-[var(--ft-muted)]">Due:</span> {selected.due_date}</div>}
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--ft-border)]">
              {selected.lines.map((l, i) => (
                <div key={i} className="flex justify-between py-1 text-sm">
                  <div>{l.description} <span className="text-[var(--ft-muted)]">× {l.quantity}</span></div>
                  <div className="font-semibold">{money(l.total)}</div>
                </div>
              ))}
              <div className="flex justify-between mt-3 pt-3 border-t border-[var(--ft-border)]">
                <div className="font-bold">Total</div>
                <div className="font-bold">{money(selected.total)}</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="ft-label">Status</div>
              <div className="flex flex-wrap gap-2">
                {["draft", "sent", "paid", "overdue", "void"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected, s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected.status === s ? "text-white border-[var(--ft-blue)]" : "border-[var(--ft-border)] hover:border-[var(--ft-blue)]"}`}
                    style={selected.status === s ? { background: "var(--ft-blue)" } : {}}
                    data-testid={`inv-status-${s}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminInvoices;
